import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { useAuth } from '../contexts/AuthContext.js';
import { authClient, GOOGLE_LOGIN_ENABLED } from '../lib/authClient.js';
import { Fingerprint } from 'lucide-react';
import ForgotPasswordModal from '../components/ForgotPasswordModal.js';
import { PasswordInput } from '../components/PasswordInput.js';

import Brand from '../components/Brand';
interface Props {
  onLoginSuccess?: () => void;
}

export default function LoginPage({ onLoginSuccess }: Props) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [mode, setMode] = useState<'password' | 'magic' | 'totp'>('password');
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [info, setInfo] = useState('');

  const finish = async () => {
    // Refresh auth context BEFORE navigating so ProtectedRoute
    // sees the user immediately and doesn't redirect back to /login
    await refresh();
    if (onLoginSuccess) onLoginSuccess();
    else navigate('/');
  };

  const genericError = t('common.errors.genericRetry', 'Ein Fehler ist aufgetreten. Bitte erneut versuchen.');
  const invalidError = t('auth.login.errors.invalid', 'Ungültige E-Mail oder Passwort');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsSubmitting(true);
    try {
      if (mode === 'magic') {
        const { error: err } = await authClient.signIn.magicLink({ email, callbackURL: '/' });
        if (err) setError(err.message || genericError);
        else setInfo(t('auth.login.magicSent', 'Wenn ein Konto zu dieser E-Mail gehört, haben wir dir einen Anmelde-Link geschickt.'));
        return;
      }
      const { data, error: err } = await authClient.signIn.email({ email, password });
      if (err) {
        setError(err.status === 429 ? t('auth.login.errors.rateLimited', 'Zu viele Versuche. Bitte warte kurz.') : invalidError);
        return;
      }
      if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
        setMode('totp');
        return;
      }
      await finish();
    } catch {
      setError(genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSecondFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const { error: err } = useBackup
        ? await authClient.twoFactor.verifyBackupCode({ code })
        : await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, '') });
      if (err) {
        setError(t('auth.login.errors.code', 'Der Code ist ungültig oder abgelaufen.'));
        return;
      }
      await finish();
    } catch {
      setError(genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasskey = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const res = await authClient.signIn.passkey();
      if (res?.error) setError(t('auth.login.errors.passkey', 'Passkey-Anmeldung fehlgeschlagen.'));
      else if (res?.data) await finish();
    } catch {
      setError(genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    await authClient.signIn.social({ provider: 'google', callbackURL: '/', errorCallbackURL: '/login' });
  };

  const inputCls =
    'block w-full rounded-lg bg-panel border border-line px-4 py-3 text-sm text-ink placeholder:text-ink-faint focus:border-accent-500/60 focus:outline-none focus:ring-1 focus:ring-accent-500/30 transition';

  return (
    <>
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
        {/* Background glow */}
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-accent-600/10 blur-[120px]" />
        </div>

        <div className="relative w-full max-w-sm space-y-6">
          {/* Logo */}
          <div className="text-center">
            <div className="mb-5">
              <Brand size="lg" />
            </div>
            <h1 className="text-2xl font-black text-ink">
              {t('auth.login.title', 'Willkommen zurück')}
            </h1>
            <p className="mt-1.5 text-sm text-ink-muted">
              {t('auth.login.subtitle')}
            </p>
          </div>

          {/* Form card */}
          <form
            onSubmit={mode === 'totp' ? handleSecondFactor : handleLogin}
            className="rounded-2xl bg-panel border border-line p-7 space-y-4 shadow-xl shadow-black/20"
          >
            {mode === 'totp' ? (
              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1.5">
                  {useBackup
                    ? t('auth.login.backupCode', 'Backup-Code')
                    : t('auth.login.totpCode', 'Code aus der Authenticator-App')}
                </label>
                <input
                  type="text"
                  inputMode={useBackup ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={inputCls}
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => { setUseBackup(!useBackup); setCode(''); setError(''); }}
                  className="mt-2 text-xs text-accent-700 hover:text-accent-600 dark:text-accent-300 dark:hover:text-accent-200 transition-colors"
                >
                  {useBackup
                    ? t('auth.login.useTotp', 'Stattdessen Authenticator-Code verwenden')
                    : t('auth.login.useBackup', 'Backup-Code verwenden')}
                </button>
              </div>
            ) : (
              <>
            <div>
              <label className="block text-xs font-medium text-ink-muted mb-1.5">
                {t('common.fields.email', 'E-Mail')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.placeholders.email', 'du@firma.de')}
                className={inputCls}
                autoFocus
                required
              />
            </div>

              {mode === 'password' && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-ink-muted">
                  {t('auth.login.placeholders.password', 'Passwort')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-accent-700 hover:text-accent-600 dark:text-accent-300 dark:hover:text-accent-200 transition-colors"
                >
                  {t('auth.login.forgotPassword', 'Passwort vergessen?')}
                </button>
              </div>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
                required
              />
            </div>

              )}
              </>
            )}

            {info && (
              <div className="rounded-lg bg-raised border border-line px-4 py-2.5 text-sm text-ink-muted">{info}</div>
            )}

            {error && (
              <div className="rounded-lg bg-danger-100 dark:bg-danger-500/10 border border-danger-500/20 px-4 py-2.5 text-sm text-danger-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-full bg-accent-600 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity shadow-lg shadow-accent-900/30"
            >
              {isSubmitting
                ? t('common.states.loading', 'Anmelden…')
                : mode === 'magic'
                  ? t('auth.login.sendLink', 'Anmelde-Link senden')
                  : mode === 'totp'
                    ? t('auth.login.verify', 'Bestätigen')
                    : t('common.actions.login', 'Anmelden')}
            </button>

            {mode !== 'totp' && (
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={handlePasskey}
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-line py-2.5 text-sm font-medium text-ink hover:bg-raised transition-colors"
                >
                  <Fingerprint className="h-4 w-4" />
                  {t('auth.login.passkey', 'Mit Passkey anmelden')}
                </button>
                {GOOGLE_LOGIN_ENABLED && (
                  <button
                    type="button"
                    onClick={handleGoogle}
                    className="w-full rounded-full border border-line py-2.5 text-sm font-medium text-ink hover:bg-raised transition-colors"
                  >
                    {t('auth.login.google', 'Mit Google anmelden')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'magic' ? 'password' : 'magic'); setError(''); setInfo(''); }}
                  className="w-full text-xs text-accent-700 hover:text-accent-600 dark:text-accent-300 dark:hover:text-accent-200 transition-colors"
                >
                  {mode === 'magic'
                    ? t('auth.login.usePassword', 'Mit Passwort anmelden')
                    : t('auth.login.useMagic', 'Anmelde-Link per E-Mail erhalten')}
                </button>
              </div>
            )}
          </form>

          <p className="text-center text-xs text-ink-faint">
            {t("common.footer.copyright", "© " + new Date().getFullYear() + " Visitenkarl · Alle Rechte vorbehalten").replace("{year}", new Date().getFullYear().toString())}
          </p>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
}
